"use client";

import { ICarouselComponent } from "@faire/web-api/indigofair/data/ICarouselComponent";
import { ICarouselStyle } from "@faire/web-api/indigofair/data/ICarouselStyle";
import { ITile } from "@faire/web-api/indigofair/data/ITile";
import {
  Carousel,
  ITEM_PER_SLIDE_DEFAULT,
} from "@faire/web/slate-labs/Carousel/Carousel";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useFilterStore } from "@faire/retailer/components/Filters/useFilterStore";
import { SDUIOnViewActionsHandler } from "@faire/retailer/components/SDUI/__internal__/ActionHandler/SDUIOnViewActionHandler";
import { logLegacyViewEvent } from "@faire/retailer/components/SDUI/__internal__/EventLogging/logLegacyViewEvent";
import { logSDUIEvent } from "@faire/retailer/components/SDUI/__internal__/EventLogging/logSDUIEvent";
import { SDUIProductTileImpressionProvider } from "@faire/retailer/components/SDUI/__internal__/SDUIProductTileImpressionProvider";
import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/retailer/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ActionLoggingEventParams,
  SDUIComponentInstanceRendererProps,
} from "@faire/retailer/components/SDUI/__internal__/types";
import {
  getCarouselControlsVariant,
  getCarouselDragFree,
  getCarouselLoop,
  getCarouselShowNavigationArrows,
  getCarouselTileSpacing,
} from "@faire/retailer/components/SDUI/ComponentRenderers/Carousel/__internal__/carouselStyleUtils";
import * as carouselStyles from "@faire/retailer/components/SDUI/ComponentRenderers/Carousel/__internal__/styles.css";
import { SDUITileRenderer } from "@faire/retailer/components/SDUI/ComponentRenderers/Tile/SDUITileRenderer";
import { UserFeedbackContextStoreProvider } from "@faire/retailer/components/UserFeedback/UserFeedbackProductTileContext";
import { useMaybeAssignCategoryTreeExperiment } from "@faire/retailer/lib/useMaybeAssignCategoryTreeExperiment";
import { useRecordMilestoneByDay } from "@faire/retailer/stores/domain/useMilestone";

interface IProps
  extends Pick<
    SDUIComponentInstanceRendererProps,
    "loggingData" | "renderBehaviour"
  > {
  tiles: ITile[];
  style: ICarouselComponent["style"];
  numberOfRows?: number;
}

const DEFAULT_SDUI_CAROUSEL_PEEK_NEXT_PERCENT = {
  mobileAndAbove: 20,
  tabletAndAbove: 0,
};

export const SDUICarousel: React.FC<IProps> = ({
  tiles,
  style,
  numberOfRows = 1,
  loggingData,
  renderBehaviour,
}) => {
  const history = useHistory();
  const recordMilestoneOnceByDay = useRecordMilestoneByDay();
  const { clearAllFilters } = useFilterStore([]);
  const maybeAssignCategoryTreeExperiment =
    useMaybeAssignCategoryTreeExperiment();

  const onVisibleCallback = (newVisibleColumns: ITile[][]) => {
    const items = newVisibleColumns.flat();
    const actionContent = items
      .map((item) => item.logging_data?.instance_identifier)
      .join(" ");
    const actionPositions = items
      .map((item) => item.logging_data?.tile_position)
      .join(" ");

    items.forEach((item) => {
      SDUIOnViewActionsHandler(
        item.on_view ?? [],
        loggingData,
        history,
        recordMilestoneOnceByDay,
        clearAllFilters,
        maybeAssignCategoryTreeExperiment
      );
    });

    logSDUIEvent(
      "VIEW",
      ActionLoggingEventParams.build({
        ...loggingData,
        action_content: actionContent,
        action_positions: actionPositions,
      })
    );

    if (loggingData.view_display_page) {
      // TODO: https://fairewholesale.atlassian.net/browse/FD-217518
      // logLegacyViewEvent should be removed once banners and grids are fully migrated to SDUI
      // and data pipelines and dashboards no longer need the layout element events
      logLegacyViewEvent(loggingData.view_display_page, {
        componentName: loggingData.component_name ?? "",
        requestId: loggingData.request_id ?? "",
        actionContent,
      });
    }
  };

  const {
    loop_behavior,
    navigation_control,
    tile_spacing,
    transition_style,
    tile_width = ICarouselStyle.ITileWidth.build(),
  } = style ?? ICarouselStyle.build();

  const { fractional } = tile_width;
  const responsiveTilesPerSlide: ResponsiveValues<number> | undefined =
    fractional?.responsive_tiles_per_slide
      ? convertServerResponsivePropertyToClientResponsiveProperty(
          ITEM_PER_SLIDE_DEFAULT.mobileAndAbove,
          (value) => Math.floor(value),
          fractional?.responsive_tiles_per_slide
        )
      : undefined;
  const responsivePeekNextPercent: ResponsiveValues<number> | undefined =
    fractional?.responsive_tiles_per_slide
      ? convertServerResponsivePropertyToClientResponsiveProperty(
          ITEM_PER_SLIDE_DEFAULT.mobileAndAbove,
          (value) => Math.round((value % 1) * 100),
          fractional?.responsive_tiles_per_slide
        )
      : DEFAULT_SDUI_CAROUSEL_PEEK_NEXT_PERCENT;

  const isFreeFlow = tile_width?.content !== undefined;

  const chunkedTiles = useMemo(
    () => chunkTiles(tiles, numberOfRows),
    [tiles, numberOfRows]
  );

  return (
    <SDUIProductTileImpressionProvider loggingData={loggingData}>
      <UserFeedbackContextStoreProvider>
        <Carousel
          items={chunkedTiles}
          itemsPerSlide={responsiveTilesPerSlide}
          peekNextPercent={responsivePeekNextPercent}
          renderItem={(tilesColumn) => {
            if (tilesColumn.length > 1) {
              return (
                <div className={carouselStyles.carouselTileColumn}>
                  {tilesColumn.map((tile) => (
                    <SDUITileRenderer
                      key={tile.logging_data?.instance_identifier}
                      tileElement={tile}
                      loggingData={loggingData}
                      renderBehaviour={renderBehaviour}
                    />
                  ))}
                </div>
              );
            } else if (tilesColumn[0]) {
              return (
                <SDUITileRenderer
                  tileElement={tilesColumn[0]}
                  loggingData={loggingData}
                  renderBehaviour={renderBehaviour}
                />
              );
            }

            return null;
          }}
          itemGap={getCarouselTileSpacing(tile_spacing)}
          loop={getCarouselLoop(loop_behavior)}
          dragFree={getCarouselDragFree(transition_style)}
          showNavigationArrows={getCarouselShowNavigationArrows(
            navigation_control
          )}
          arrowColorOrVariant={getCarouselControlsVariant(navigation_control)}
          shouldShowDisabledNavigationArrows
          onVisible={onVisibleCallback}
          freeFlow={isFreeFlow}
          arrowOffsetX={0}
          attachRefOnView={renderBehaviour?.deferInitializationUntilView}
        />
      </UserFeedbackContextStoreProvider>
    </SDUIProductTileImpressionProvider>
  );
};

const chunkTiles = (tiles: ITile[], numberOfRows: number): ITile[][] => {
  return tiles.reduce<ITile[][]>((arr, item, idx) => {
    return idx % numberOfRows === 0
      ? [...arr, [item]]
      : [...arr.slice(0, -1), [...(arr.slice(-1)[0] ?? []), item]];
  }, []);
};
